/**
 * Friends List Block Component
 * 
 * Shows list of invited friends with status and rewards earned.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Users, UserCheck, UserPlus, ChevronLeft, ChevronRight, Clock, CheckCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { cn } from '../../lib/utils'
import type { ReferredFriend } from '../../types'

interface FriendsListBlockProps {
  referrals: ReferredFriend[]
  totalReferrals: number
  activeReferrals: number
}

const PAGE_SIZE = 5

export function FriendsListBlock({ referrals, totalReferrals, activeReferrals }: FriendsListBlockProps) {
  const { t } = useTranslation('common')
  const [page, setPage] = useState(0)
  
  const totalPages = Math.ceil(referrals.length / PAGE_SIZE)
  const paginatedReferrals = referrals.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getStatusBadge = (status: string) => {
    if (status === 'active') {
      return (
        <Badge className="status-badge bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
          <CheckCircle className="h-3 w-3 mr-1" />
          {t('referrals.statusActive', 'Active')}
        </Badge>
      )
    }
    return (
      <Badge variant="secondary" className="status-badge">
        <Clock className="h-3 w-3 mr-1" />
        {t('referrals.statusPending', 'Pending')}
      </Badge>
    )
  }

  return (
    <Card className="friends-list-block">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-500" />
            {t('referrals.invitedFriends', 'Invited Friends')}
          </div>
          <div className="stats-badges flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              <UserPlus className="h-3 w-3 mr-1" />
              {totalReferrals} {t('referrals.total', 'total')}
            </Badge>
            <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-xs">
              <UserCheck className="h-3 w-3 mr-1" />
              {activeReferrals} {t('referrals.active', 'active')}
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        {referrals.length === 0 ? (
          <div className="empty-state text-center py-8">
            <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">
              {t('referrals.noFriendsYet', "You haven't invited any friends yet.")}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {t('referrals.shareYourLink', 'Share your referral link to get started!')}
            </p>
          </div>
        ) : (
          <>
            {/* Friends list */}
            <div className="friends-list space-y-3">
              {paginatedReferrals.map(referral => (
                <div 
                  key={referral.id}
                  className={cn(
                    "friend-item flex items-center justify-between p-3 rounded-lg border transition-all",
                    referral.status === 'active' 
                      ? "bg-emerald-500/5 border-emerald-500/20" 
                      : "bg-card border-border"
                  )}
                >
                  <div className="friend-info flex items-center gap-3">
                    <div className={cn(
                      "avatar w-10 h-10 rounded-full flex items-center justify-center",
                      referral.status === 'active' 
                        ? "bg-emerald-500/10" 
                        : "bg-muted"
                    )}>
                      {referral.status === 'active' ? (
                        <UserCheck className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <UserPlus className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {referral.referred_name || referral.referred_email_masked || t('referrals.anonymous', 'Anonymous')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('referrals.joinedOn', 'Joined')} {formatDate(referral.signed_up_at)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="friend-status flex items-center gap-3">
                    {getStatusBadge(referral.status)}
                    {referral.became_active_at && (
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        {t('referrals.activeSince', 'Active since')} {formatDate(referral.became_active_at)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination flex items-center justify-center gap-2 mt-4 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground px-3">
                  {page + 1} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page === totalPages - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

